import React, { useRef, useEffect } from 'react';
import './HeroSection.css';

const BRAND_GRAD = 'linear-gradient(90deg,#6366F1,#8B5CF6 55%,#D946EF)';
const DOC_EDGE   = 'linear-gradient(135deg, #d6d9e7, #a4a8bf)';
const DASH_EDGE  = 'linear-gradient(135deg, #261a48, #140b29)';
const SKEW_A     = 'rotateY(-27deg) rotateX(13deg)';

function bar(w, c, h) {
  return <div style={{ height: h || 4, width: w, borderRadius: 2, background: c || 'rgba(19,16,34,0.24)' }} />;
}

function spark(pts, h, w2, stroke) {
  const max = Math.max(...pts), min = Math.min(...pts);
  const span = max - min || 1;
  const step = w2 / (pts.length - 1);
  const xy = pts.map((p, i) => [i * step, h - ((p - min) / span) * h]);
  const linePath = xy.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const areaPath = linePath + ' L' + w2 + ' ' + h + ' L0 ' + h + ' Z';
  return (
    <svg width={w2} height={h} viewBox={'0 0 ' + w2 + ' ' + h} style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id={'ocg-' + stroke} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#6366F1" />
          <stop offset="0.5" stopColor="#8B5CF6" />
          <stop offset="1" stopColor="#D946EF" />
        </linearGradient>
        <linearGradient id={'ocf-' + stroke} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="rgba(139,92,246,0.35)" />
          <stop offset="1" stopColor="rgba(139,92,246,0)" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={'url(#ocf-' + stroke + ')'} />
      <path d={linePath} fill="none" stroke={'url(#ocg-' + stroke + ')'} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function miniTile(i, frac) {
  return (
    <div key={i} style={{ flex: 1, background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: '10px 11px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ width: 22 + i * 6, height: 5, borderRadius: 3, background: BRAND_GRAD }} />
      <div style={{ width: '62%', height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.18)' }} />
      <div style={{ marginTop: 2, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.10)', overflow: 'hidden' }}>
        <div style={{ width: (frac * 100) + '%', height: '100%', borderRadius: 2, background: BRAND_GRAD }} />
      </div>
    </div>
  );
}

function barCol(hv, i) {
  return (
    <div key={i} style={{ flex: 1, height: hv, borderRadius: 3, alignSelf: 'flex-end', background: i % 3 === 2 ? BRAND_GRAD : 'rgba(255,255,255,0.14)', boxShadow: i % 3 === 2 ? '0 0 14px rgba(139,92,246,0.5)' : 'none' }} />
  );
}

function analytics(w) {
  return (
    <div style={{ width: w, background: 'linear-gradient(160deg, rgba(28,20,52,0.96), rgba(18,12,32,0.96))', border: '1px solid rgba(129,107,255,0.32)', borderRadius: 16, padding: 18, boxShadow: '0 50px 100px -34px rgba(99,102,241,0.55), inset 0 1px 0 rgba(255,255,255,0.06)' }}>
      <div style={{ display: 'flex', gap: 9, marginBottom: 14 }}>
        {miniTile(0, 0.8)}{miniTile(1, 0.55)}{miniTile(2, 0.92)}
      </div>
      <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '14px 14px 8px', marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ width: 70, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.22)' }} />
          <div style={{ width: 34, height: 6, borderRadius: 3, background: BRAND_GRAD }} />
        </div>
        {spark([12, 20, 16, 28, 24, 40, 34, 52, 46, 64], 78, w - 64, 'a')}
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ flex: 1.5, background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 14, display: 'flex', gap: 6, alignItems: 'flex-end', height: 96 }}>
          {[28, 44, 36, 60, 48, 72, 58].map((hv, i) => barCol(hv, i))}
        </div>
        <div style={{ flex: 1, background: 'rgba(255,255,255,0.04)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 76, height: 76, borderRadius: '50%', background: 'conic-gradient(#6366F1 0turn, #8B5CF6 0.62turn, #D946EF 0.62turn 0.84turn, rgba(255,255,255,0.10) 0.84turn 1turn)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 46, height: 46, borderRadius: '50%', background: '#180C2E', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)' }} />
          </div>
        </div>
      </div>
    </div>
  );
}

function paper(w, opts) {
  opts = opts || {};
  const nRows = opts.rows || 9;
  const rows = [];
  for (let r = 0; r < nRows; r++) {
    const cells = [];
    for (let c = 0; c < 8; c++) {
      const check = c === 6;
      cells.push(
        <div key={c} style={{ flex: c === 0 ? '0 0 18%' : '1', height: 12, borderRight: '1px solid rgba(19,16,34,0.07)', borderBottom: '1px solid rgba(19,16,34,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {check
            ? <div style={{ width: 6, height: 6, borderRadius: 1, background: '#02A783' }} />
            : bar((40 + ((r * 7 + c * 13) % 46)) + '%', 'rgba(19,16,34,0.22)')}
        </div>
      );
    }
    rows.push(<div key={r} style={{ display: 'flex' }}>{cells}</div>);
  }
  const metaRow = (k) => (
    <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 9px', borderBottom: '1px solid rgba(19,16,34,0.06)' }}>
      {bar('26%', 'rgba(19,16,34,0.34)')}
      <div style={{ flex: 1 }} />
      {bar('30%', 'rgba(19,16,34,0.16)')}
    </div>
  );
  const extraStyle = opts.style || {};
  return (
    <div style={{ width: w, background: 'linear-gradient(180deg,#ffffff,#eef1fb)', borderRadius: 9, padding: '16px 16px 18px', border: '1px solid rgba(255,255,255,0.8)', boxShadow: '0 40px 80px -28px rgba(0,0,0,0.6)', position: 'relative', overflow: 'hidden', ...extraStyle }}>
      <div style={{ position: 'absolute', left: 0, right: 0, height: 26, pointerEvents: 'none', background: 'linear-gradient(180deg, rgba(99,102,241,0), rgba(129,107,255,0.45), rgba(99,102,241,0))', animation: 'oc-beam 5.5s ease-in-out infinite', mixBlendMode: 'screen' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 9 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {bar(86, 'rgba(99,102,241,0.55)', 7)}
          {bar(56, 'rgba(19,16,34,0.2)')}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'flex-end' }}>
          {bar(64, 'rgba(19,16,34,0.18)')}
          {bar(44, 'rgba(19,16,34,0.13)')}
        </div>
      </div>
      <div style={{ height: 12, borderRadius: 2, background: 'rgba(19,16,34,0.09)', marginBottom: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {bar('48%', 'rgba(19,16,34,0.30)', 5)}
      </div>
      <div style={{ border: '1px solid rgba(19,16,34,0.09)', borderRadius: 4, marginBottom: 9 }}>
        {metaRow(0)}{metaRow(1)}{metaRow(2)}
      </div>
      <div style={{ border: '1px solid rgba(19,16,34,0.10)', borderRadius: 4, overflow: 'hidden' }}>
        {rows}
      </div>
    </div>
  );
}

function slab3d(w, slices, per, edge, radius, front) {
  return (
    <div style={{ position: 'relative', width: w, transformStyle: 'preserve-3d' }}>
      {Array.from({ length: slices }).map((_, k) => {
        const i = slices - k;
        return (
          <div key={'sl' + i} style={{ position: 'absolute', inset: 0, borderRadius: radius, transform: 'translateZ(' + (-i * per) + 'px)', background: edge, boxShadow: i === slices ? '0 70px 120px -34px rgba(0,0,0,0.8)' : 'none' }} />
        );
      })}
      {front}
    </div>
  );
}

function plane(pos, anim, rot, child) {
  return (
    <div style={Object.assign({ position: 'absolute', animation: anim }, pos)}>
      <div style={{ transform: 'perspective(1500px) ' + rot, transformStyle: 'preserve-3d', transformOrigin: 'center' }}>
        {child}
      </div>
    </div>
  );
}

function HeroVisual() {
  const hvRef = useRef(null);

  useEffect(() => {
    const hv = hvRef.current;
    if (!hv) return;
    const check = () => {
      const rect = hv.getBoundingClientRect();
      const pct = (window.innerHeight / 2 - rect.top - rect.height / 2) / window.innerHeight;
      hv.style.transform = 'translateY(' + (pct * -48) + 'px)';
    };
    window.addEventListener('scroll', check, { passive: true });
    return () => window.removeEventListener('scroll', check);
  }, []);

  return (
    <div id="oc-hero-visual" ref={hvRef} style={{ position: 'relative', minHeight: 520 }}>
      <div style={{ position: 'absolute', left: '8%', top: '12%', width: '84%', height: '76%', borderRadius: '50%', background: 'radial-gradient(ellipse at center, rgba(99,102,241,0.28), rgba(217,70,239,0.05) 55%, rgba(99,102,241,0) 72%)', filter: 'blur(6px)', zIndex: 0 }} />
      {plane({ right: -18, top: 96, width: 400, zIndex: 1 }, 'oc-floatB 11s ease-in-out infinite', SKEW_A,
        slab3d(400, 11, 2.6, DASH_EDGE, 16, analytics(400)))}
      {/* showConnectors = false */}
      {plane({ left: 26, top: 12, width: 308, zIndex: 5 }, 'oc-floatA 9s ease-in-out infinite', SKEW_A,
        slab3d(308, 18, 1.9, DOC_EDGE, 9, paper(308, { rows: 16 })))}
    </div>
  );
}

function HeroSection() {
  return (
    <section style={{ position: 'relative', background: '#131022', overflow: 'hidden', padding: '84px 56px 96px' }}>
      <div style={{ position: 'absolute', left: '50%', top: -440, transform: 'translateX(-50%)', width: 1700, height: 900, background: 'radial-gradient(ellipse at center, rgba(99,102,241,0.20), rgba(99,102,241,0) 60%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', right: -200, top: 120, width: 760, height: 760, background: 'radial-gradient(ellipse at center, rgba(217,70,239,0.10), rgba(217,70,239,0) 62%)', pointerEvents: 'none' }} />
      <div style={{ position: 'relative', maxWidth: 1240, margin: '0 auto', display: 'grid', gridTemplateColumns: 'minmax(0,0.86fr) minmax(0,1.14fr)', gap: 48, alignItems: 'center' }} className="oc-hero-grid">
        <div>
          <div className="oc-reveal" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <img src={`${process.env.PUBLIC_URL}/assets/img/apps/oculo/Oculo(366 x 96 px).png`} alt="Oculo" style={{ height: 42, width: 'auto', display: 'block' }} onError={e => { e.target.style.display = 'none'; }} />
            <span style={{ width: 1, height: 26, background: 'rgba(255,255,255,0.16)' }} />
            <span style={{ font: '700 11.5px Manrope,sans-serif', letterSpacing: '0.14em', color: '#B6BCFF' }}>AI NDT DOCUMENT EXTRACTION</span>
          </div>
          <h1 className="oc-reveal" style={{ margin: '24px 0 0', fontFamily: 'Montserrat,sans-serif', fontWeight: 800, fontSize: 54, lineHeight: 1.08, letterSpacing: '-0.02em', color: '#fff' }}>
            Read the report.<br />Return the data.<br />
            <span style={{ background: BRAND_GRAD, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>Verify in seconds.</span>
          </h1>
          <p className="oc-reveal" style={{ margin: '26px 0 0', maxWidth: 520, fontFamily: 'Montserrat,sans-serif', fontSize: 18, lineHeight: 1.62, color: 'rgba(255,255,255,0.72)' }}>
            Purpose-built AI for NDT inspection reports. Oculo reads dense documentation at{' '}
            <span style={{ color: '#fff', fontWeight: 700 }}>99.5%+ field-level accuracy</span> and returns a digital twin you can verify in seconds.
          </p>
          <div className="oc-reveal oc-hero-stats" style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 20, marginTop: 34 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ font: '800 34px Manrope,sans-serif', lineHeight: 1, whiteSpace: 'nowrap', background: BRAND_GRAD, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>99.5%+</span>
              <span style={{ font: '500 11.5px Manrope,sans-serif', color: 'rgba(255,255,255,0.50)' }}>field-level accuracy</span>
            </div>
            <div className="oc-stat-divider" style={{ width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,0.12)' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ font: '700 15px Manrope,sans-serif', lineHeight: 1.25, whiteSpace: 'nowrap', color: '#fff' }}>Every NDT modality</span>
              <span style={{ font: '500 11.5px Manrope,sans-serif', color: 'rgba(255,255,255,0.50)' }}>RT · UT · MT · PT · VT</span>
            </div>
            <div className="oc-stat-divider" style={{ width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,0.12)' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ font: '700 15px Manrope,sans-serif', lineHeight: 1.25, whiteSpace: 'nowrap', color: '#fff' }}>NDT · Welding · Integrity</span>
              <span style={{ font: '500 11.5px Manrope,sans-serif', color: 'rgba(255,255,255,0.50)' }}>every fixed-layout report</span>
            </div>
          </div>
        </div>
        <div className="oc-reveal">
          <HeroVisual />
        </div>
      </div>
    </section>
  );
}

export default HeroSection;
